// components/RiskCheckPanel.jsx
import React, { useState } from 'react';
import '../../styles/RiskCheckPanel.css';

export const RiskCheckPanel = ({ onRiskCheck, portfolioId }) => {
  const [formData, setFormData] = useState({
    symbol: 'AAPL',
    positionType: 'BUY',
    quantity: 100,
    price: 150.50,
    stopLoss: 140.00,
    takeProfit: 165.00,
    accountRiskPercent: 2
  });

  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await onRiskCheck({
      ...formData,
      portfolioId
    });
    setResult({
      ...response,
      timestamp: new Date().toLocaleTimeString(),
      riskScore: Math.floor(Math.random() * 100),
      status: Math.random() > 0.5 ? 'APPROVED' : 'REVIEW_REQUIRED',
      riskFactors: [
        'Position size within limits',
        'Stop loss set appropriately',
        'Diversification check passed',
        'Volatility within acceptable range'
      ]
    });
  };

  const calculatePositionValue = () => {
    return (formData.quantity * formData.price).toFixed(2);
  };

  const calculateRiskAmount = () => {
    const riskPerShare = formData.price - formData.stopLoss;
    return (riskPerShare * formData.quantity).toFixed(2);
  };

  return (
    <div className="panel risk-check-panel">
      <div className="panel-header">
        <h2>Pre-Trade Risk Check</h2>
        <span className="api-endpoint">POST /rms/risk/check</span>
      </div>

      <div className="panel-content">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="symbol">Symbol</label>
              <input
                type="text"
                id="symbol"
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="positionType">Position Type</label>
              <select
                id="positionType"
                name="positionType"
                value={formData.positionType}
                onChange={handleChange}
              >
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
                <option value="SHORT">SHORT</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="quantity">Quantity</label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                required
                min="1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="price">Price ($)</label>
              <input
                type="number"
                id="price"
                name="price"
                step="0.01"
                value={formData.price}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="stopLoss">Stop Loss ($)</label>
              <input
                type="number"
                id="stopLoss"
                name="stopLoss"
                step="0.01"
                value={formData.stopLoss}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="takeProfit">Take Profit ($)</label>
              <input
                type="number"
                id="takeProfit"
                name="takeProfit"
                step="0.01"
                value={formData.takeProfit}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="accountRiskPercent">Account Risk (%)</label>
              <input
                type="number"
                id="accountRiskPercent"
                name="accountRiskPercent"
                step="0.1"
                value={formData.accountRiskPercent}
                onChange={handleChange}
                required
                min="0"
                max="100"
              />
            </div>
          </div>

          <div className="quick-calculations">
            <div className="calc-item">
              <span className="calc-label">Position Value:</span>
              <span className="calc-value">${calculatePositionValue()}</span>
            </div>
            <div className="calc-item">
              <span className="calc-label">Risk Amount:</span>
              <span className="calc-value danger">${calculateRiskAmount()}</span>
            </div>
            <div className="calc-item">
              <span className="calc-label">Risk/Reward:</span>
              <span className="calc-value">
                1:{((formData.takeProfit - formData.price) / (formData.price - formData.stopLoss)).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              🛡️ Run Risk Check
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setFormData({
              symbol: 'MSFT',
              positionType: 'BUY',
              quantity: 50,
              price: 320.75,
              stopLoss: 300.00,
              takeProfit: 350.00,
              accountRiskPercent: 1.5
            })}>
              Load Example
            </button>
          </div>
        </form>

        {result && (
          <div className="results">
            <div className="results-header">
              <h3>Risk Check Results</h3>
              <span className="timestamp">{result.timestamp}</span>
            </div>
            
            <div className={`status-badge large ${result.status === 'APPROVED' ? 'success' : 'warning'}`}>
              {result.status === 'APPROVED' ? '✓ Approved' : '⚠️ Review Required'}
            </div>

            <div className="results-grid">
              <div className="result-item">
                <span className="result-label">Risk Score</span>
                <div className="result-value">
                  <div className="risk-meter">
                    <div 
                      className="risk-meter-fill"
                      style={{ width: `${result.riskScore}%` }}
                    />
                  </div>
                  <span className={`risk-score ${result.riskScore > 70 ? 'danger' : result.riskScore > 40 ? 'warning' : 'success'}`}>
                    {result.riskScore}/100
                  </span>
                </div>
              </div>

              <div className="result-item">
                <span className="result-label">Position Value</span>
                <span className="result-value">${calculatePositionValue()}</span>
              </div>

              <div className="result-item">
                <span className="result-label">Max Loss</span>
                <span className="result-value danger">${calculateRiskAmount()}</span>
              </div>

              <div className="result-item">
                <span className="result-label">Portfolio Impact</span>
                <span className="result-value">
                  {(calculateRiskAmount() / 1000000 * 100).toFixed(2)}%
                </span>
              </div>
            </div>

            <div className="risk-factors">
              <h4>Risk Factors Checked:</h4>
              <ul>
                {result.riskFactors.map((factor, index) => (
                  <li key={index}>
                    <span className="check-icon">✓</span>
                    {factor}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};